import { z } from 'zod';
import { dateString } from './training.js';

/**
 * Players and parents saying a player can't make it: one session or game at a time, or "away"
 * for a stretch of dates (every session and game in it, on every team that player is on,
 * including ones added later). "Coming after all" on one session overrides an away stretch.
 */

/** A roster player linked to you: yourself, or your child, on one team. */
export interface MyPlayer {
  playerId: string;
  name: string;
  teamId: string;
  teamName: string;
  kind: 'player' | 'parent';
}

/** Whether one of your players is coming to one session or game. */
export interface PlayerAvailability {
  playerId: string;
  name: string;
  unavailable: boolean;
  reason: string | null;
  /** Set when an away stretch covers this date (and wasn't overridden). */
  away: { endDate: string; reason: string | null } | null;
}

/** Your players' answers for sessions and games in a date range (the Schedule). */
export interface MyScheduleAvailability {
  sessions: Record<string, PlayerAvailability[]>;
  games: Record<string, PlayerAvailability[]>;
}

/** For coaches: who's out of each session and game in a date range. */
export interface AwayCounts {
  sessions: Record<string, { name: string; reason: string | null }[]>;
  games: Record<string, { name: string; reason: string | null }[]>;
}

/** An away stretch you've set, for one person (on each of their teams). */
export interface AwayPeriod {
  groupId: string;
  name: string;
  teamNames: string[];
  startDate: string;
  endDate: string;
  reason: string | null;
}

export const awayInput = z
  .object({
    playerIds: z.array(z.string().uuid()).min(1, 'Choose who’s away.').max(20),
    startDate: dateString,
    endDate: dateString,
    reason: z.string().trim().max(200, 'Keep the reason under 200 characters.').nullish().transform((v) => v || null),
  })
  .refine((a) => a.startDate <= a.endDate, { message: 'The last day has to be on or after the first.', path: ['endDate'] })
  .refine((a) => (Date.parse(a.endDate) - Date.parse(a.startDate)) / 86_400_000 <= 366, { message: 'Keep it under a year.', path: ['endDate'] });
export type AwayInput = z.input<typeof awayInput>;

/** Same shape as a session's: one player, can't make it (or can, after all). */
export const gameAvailabilityInput = z.object({
  playerId: z.string().uuid(),
  unavailable: z.boolean(),
  reason: z.string().trim().max(200, 'Keep the reason under 200 characters.').nullish().transform((v) => v || null),
});

/** People are grouped by name across teams: the same child on two teams is one person. */
export function groupPeople(players: MyPlayer[]): { key: string; name: string; playerIds: string[]; teamNames: string[] }[] {
  const out = new Map<string, { key: string; name: string; playerIds: string[]; teamNames: string[] }>();
  for (const p of players) {
    const key = p.name.trim().toLowerCase().replace(/\s+/g, ' ');
    const g = out.get(key) ?? { key, name: p.name, playerIds: [], teamNames: [] };
    g.playerIds.push(p.playerId);
    if (!g.teamNames.includes(p.teamName)) g.teamNames.push(p.teamName);
    out.set(key, g);
  }
  return [...out.values()].sort((a, b) => a.name.localeCompare(b.name));
}
